import React, { useState } from 'react';
import { Button } from './ui/button';
import { Download, Share2, Plus, X, Smartphone } from 'lucide-react';
import usePwaInstall from '../hooks/usePwaInstall';

/**
 * Mobile-first banner that surfaces the PWA install path.
 *  - Chromium / Android: native `beforeinstallprompt` dialog.
 *  - iOS Safari: a one-time inline tutorial (tap Share → Add to Home Screen).
 *
 * Hidden when the app is already running standalone, when the user has
 * dismissed the banner in the last 7 days, or when no install path exists
 * (e.g. desktop Firefox).
 */
const InstallPwaBanner = () => {
  const {
    canPrompt, isIOS, isStandalone, installed, showHint, promptInstall, dismiss,
  } = usePwaInstall();
  const [showIosTip, setShowIosTip] = useState(false);

  if (isStandalone || installed || !showHint) return null;
  // Only render on iOS or where a native prompt is queued.
  if (!isIOS && !canPrompt) return null;

  const handleInstall = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === 'dismissed') dismiss();
    } else if (isIOS) {
      setShowIosTip((v) => !v);
    }
  };

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-50 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
      data-testid="install-pwa-banner"
    >
      <div className="rounded-xl border border-purple-200/40 dark:border-purple-700/40 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-xl p-3 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 dark:text-white">
            Install Lumera
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Open offline, save battery, never lose your sermon drafts.
          </p>

          {showIosTip && isIOS && (
            <div
              className="mt-2 text-[11px] text-slate-700 dark:text-slate-200 space-y-1.5 border-t border-border pt-2"
              data-testid="install-pwa-ios-tip"
            >
              <p className="flex items-center gap-1.5">
                <span className="font-semibold">1.</span>
                <span className="flex items-center gap-1">
                  Tap <Share2 className="w-3 h-3 inline" /> Share
                </span>
              </p>
              <p className="flex items-center gap-1.5">
                <span className="font-semibold">2.</span>
                <span className="flex items-center gap-1">
                  Choose <Plus className="w-3 h-3 inline" /> Add to Home Screen
                </span>
              </p>
              <p className="text-[10px] text-muted-foreground italic">
                The Lumera icon will appear on your Home Screen and launch full-screen.
              </p>
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-2">
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white h-8 px-3 text-xs"
              data-testid="install-pwa-install-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              {isIOS && !canPrompt
                ? (showIosTip ? 'Got it' : 'How')
                : 'Install'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              className="h-8 px-2 text-xs text-muted-foreground"
              data-testid="install-pwa-dismiss-btn"
            >
              Not now
            </Button>
          </div>
        </div>

        <button
          onClick={dismiss}
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground p-1 -mr-1"
          data-testid="install-pwa-close-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default InstallPwaBanner;
